<template>
	<Modal v-model:visible="visible" :title="$t('account.linkedTelegrams.verifyModal.header')" @cancel="closeVerifyModal">
		<Result
			v-if="verifyModalVerified"
			status="success"
			:title="$t('account.linkedTelegrams.verifyModal.verifiedText')"
		/>
		<p v-else>
			{{ $t('account.linkedTelegrams.verifyModal.p1') }}
			<TypographyText code class="nobr-inline">{{ accountInfo.telegramUsername }}</TypographyText>
			{{ $t('account.linkedTelegrams.verifyModal.p2') }}
			<TypographyLink :href="accountInfo.verificationLink" target="_blank" class="nobr-inline">{{ $t('account.linkedTelegrams.verifyModal.verificationLinkText') }}</TypographyLink>
			{{ $t('account.linkedTelegrams.verifyModal.p3') }}
			<TypographyText keyboard class="nobr-inline">START</TypographyText>
		</p>
		<template #footer>
			<template v-if="verifyModalVerified">
				<Button
					key="submit"
					type="primary"
					:loading="isLoading"
					@click="sendTestMessage"
				>
					{{ $t('account.linkedTelegrams.verifyModal.testButtonLabel') }}
				</Button>
				<Button key="back" @click="closeVerifyModal">{{ $t('account.linkedTelegrams.verifyModal.closeButtonLabel') }}</Button>
			</template>
			<Button
				v-else
				key="verify"
				type="primary"
				:href="accountInfo.verificationLink"
				target="_blank"
			>
				{{ $t('account.linkedTelegrams.verifyModal.verifyButtonLabel') }}
			</Button>
		</template>
	</Modal>
</template>

<script>
import { ref, toRef, onBeforeUnmount, computed, watch } from 'vue';
import { Modal, Result, TypographyLink, TypographyText, Button, message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';

import { showErrorMessage } from '@/components/common/errorMessage';

export default {
	components: {
		Modal, Result, TypographyLink, TypographyText, Button,
	},
	props: ['account', 'botName'],
	emits: ['update:account', 'updateAccounts'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const accountRef = toRef(props, 'account');
		const botNameRef = toRef(props, 'botName');
		const visible = computed(() => accountRef.value != null);
		const accountInfo = computed(() => {
			if (accountRef.value == null) {
				return { verificationLink: '', telegramUsername: '' };
			}
			return {
				verificationLink: 'https://t.me/' + botNameRef.value + '?start=' + accountRef.value.verificationCode,
				telegramUsername: accountRef.value.telegramUsername,
			};
		});

		const verifyStatusTimer = ref(0);
		const verifyModalVerified = ref(false);
		const checkVerificationStatusHandler = response => {
			if (response.status === 'ok' && response.verified) {
				clearInterval(verifyStatusTimer.value);
				verifyModalVerified.value = true;
				emit('updateAccounts');
			}
		};
		const checkVerificationStatus = () => {
			Api.alertsTgVerificationStatus(
				accountRef.value.telegramUsername,
				checkVerificationStatusHandler,
			)
		};
		watch(
			accountRef,
			value => {
				verifyModalVerified.value = false;
				if (value == null) {
					clearInterval(verifyStatusTimer.value);
					verifyStatusTimer.value = 0;
				} else {
					verifyStatusTimer.value = setInterval(checkVerificationStatus, 1000);
				}
			}
		)
		onBeforeUnmount(() => {
			if (verifyStatusTimer.value > 0) {
				clearInterval(verifyStatusTimer.value);
			}
		})
		const isLoading = ref(false);
		const closeVerifyModal = () => {
			emit('update:account', null);
		};
		const sendTestMessage = () => {
			isLoading.value = true;
			Api.alertsTgTest(
				accountRef.value.telegramUsername,
				response => {
					if (response.status === 'ok') {
						message.success(t('account.linkedTelegrams.accountTestSent'));
						closeVerifyModal();
					} else {
						showErrorMessage(response.errors);
					}
					isLoading.value = false;
				}
			);
		};
		return {
			visible,
			accountInfo,
			verifyModalVerified,
			isLoading,
			sendTestMessage,
			closeVerifyModal,
		};
	},
}
</script>
